import type { ComponentProps } from 'react';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { TouchableOpacity } from 'react-native-gesture-handler';
import { useLinkProps } from '@react-navigation/native';
import {
  BodyText,
  Container,
  Icon,
  LoadingPlaceholder,
  theme,
  useResponsive,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

export const ReorderRailHeader = (props: RailHeaderProps) => {
  const { hasOrders, isLoading } = props;
  const { match } = useResponsive();
  const { t } = useLocalizationContext();

  const headerTextSize: ComponentProps<typeof BodyText>['size'] = match([
    4, 3, 2,
  ]);

  // ────────────────────────────────────────────────────────────────────

  return (
    <Container style={styles.wrapper}>
      <BodyText size={headerTextSize} style={styles.title}>
        {t('home.reorder-rail-title')}
      </BodyText>

      <RailHeaderAllOrdersLink
        hasOrders={hasOrders}
        isLoading={isLoading}
        fontSize={headerTextSize}
      />
    </Container>
  );
};

const RailHeaderAllOrdersLink = (props: RailHeaderAllOrdersLinkProps) => {
  const { fontSize, isLoading, hasOrders } = props;

  const { t } = useLocalizationContext();
  const { onPress, ...linkProps } = useLinkProps({
    to: `/account/orders`,
  });

  if (isLoading) {
    return (
      <View style={styles.allOrdersLinkPlaceholder}>
        <LoadingPlaceholder
          palette="lightGreen"
          rowHeight={24}
          borderRadius={theme.spacing['4']}
        />
      </View>
    );
  }

  if (!hasOrders) return null;

  return (
    <TouchableOpacity
      style={styles.allOrdersLink}
      onPress={onPress}
      {...linkProps}
    >
      <BodyText size={fontSize} style={styles.allOrdersLinkText}>
        {t('home.reorder-rail-all-orders')}
      </BodyText>

      <Icon name="IconArrowRight" />
    </TouchableOpacity>
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type RailHeaderProps = {
  readonly hasOrders?: boolean;
  readonly isLoading: boolean;
};

type RailHeaderAllOrdersLinkProps = {
  readonly hasOrders?: boolean;
  readonly fontSize: ComponentProps<typeof BodyText>['size'];
  readonly isLoading?: boolean;
};

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  wrapper: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing['4'],
  },
  title: {
    textTransform: 'uppercase',
  },
  allOrdersLink: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  allOrdersLinkPlaceholder: {
    width: 124,
  },
  allOrdersLinkText: {
    paddingRight: 4,
  },
});
